
import { FlatList } from "react-native"
import React, { useEffect, useState } from "react";

import type { CompetitionMatchMarketProps, CompetitionMatchProps, CompetitionProps, CompetitionTypeProps, EventProps, FocusPositionProps } from "../../types";
import { ManageCompetitionHelpers } from "../api";
import { Icons, Toggle } from "../../Components";
import moment from "moment-mini";
import { useColors } from "../../constants/useColors";
import { Button, Text, TextInput, View } from "../../Components/Themed";

type ContestSettingsFormProps = {
    competition:CompetitionProps,
    onFocusPosition?:(position:FocusPositionProps) => void,
    competition_matches:CompetitionMatchProps[],
    competition_match_markets:CompetitionMatchMarketProps[],
    competition_types:CompetitionTypeProps[],
    events:EventProps[],
    init_expanded?:boolean,
    onCompetitionUpdate: (c:CompetitionProps) => void
}

const length_types:CompetitionProps['length_type'][] = ['event']

const ContestSettingsForm = ({ init_expanded, onFocusPosition, competition, competition_matches, competition_match_markets, competition_types, events, onCompetitionUpdate }:ContestSettingsFormProps) => {
    const Colors = useColors();
    const [ expanded, setExpanded ] = useState(init_expanded ?? false);
    const [ draft_competition, setDraftCompetition ] = useState<CompetitionProps | undefined>(undefined);

    const competition_type = competition_types.find(ct => ct.competition_type_id == competition?.competition_type_id);

    const start_time_options = ManageCompetitionHelpers.getStartTimeOptions(competition, competition_matches, competition_match_markets, events);

    useEffect(() => {
        setDraftCompetition(competition);
    },[competition])


    const renderStartTimeOptions = (data: { item: { start_time:string, label:string }, index:number }) => {
        if(!draft_competition){ return <></> }
        const selected = competition?.scheduled_datetime == data.item.start_time ? true : false
        return (
            <Button 
            float style={{ flexDirection:'row', margin:2, alignItems:'center', padding:10, borderColor:selected?Colors.text.success:Colors.borders.light }} onPress={() => onCompetitionUpdate({ ...draft_competition, scheduled_datetime: data.item.start_time })}>
                <View style={{ height:25, width:25, borderRadius:100, borderWidth:1, justifyContent:'center', alignItems:'center', borderColor:selected?Colors.text.success:Colors.borders.light }}>
                    {selected ?
                    <View style={{ height:15, width:15, borderRadius:100, backgroundColor:Colors.text.success}} />
                    :<></>}
                </View>
                <View transparent style={{ flex:1, marginLeft:10 }}>
                    <Text weight='bold' size={14} >{data.item.label}</Text>
                    <Text weight='semibold' style={{ marginTop:4 }} size={14} >{moment(data.item.start_time).format('MMM DD YYYY hh:mm a')}</Text>
                </View>
            </Button>
        )
    }

    if(!competition || !draft_competition){ return <></> }
    const is_changed = JSON.stringify({ max_pick_count:competition.max_pick_count, initial_balance:competition.initial_balance }) != JSON.stringify({ max_pick_count:draft_competition.max_pick_count, initial_balance:draft_competition.initial_balance }) ? true : false
    return (
        <View float style={{ flexGrow:1, borderRadius:0 }}>
            <Button
                disabled={init_expanded}
                float style={{ flexDirection:'row', borderRadius:0, padding:20, alignItems:'center', backgroundColor:Colors.toggle.button.active }} onPress={() => setExpanded(!expanded)}>
                <Icons.ChangeIcon size={24} color={Colors.toggle.text.active} />
                <View transparent style={{ flex:1, marginLeft:10, marginRight:10 }}>
                    <Text theme="h1" color={Colors.toggle.text.active}>Contest Settings</Text>
                    <Text style={{ marginTop:3 }} theme='description' color={Colors.toggle.text.active}>Manage the settings of how user will interact with the added contests</Text>
                </View>
                {is_changed ?
                <Button
                    title='SAVE'
                    type='success'
                    onPress={() => onCompetitionUpdate(draft_competition)}
                />
                : !init_expanded ?
                <Icons.ChevronIcon direction={expanded ? 'up' : 'down'} color={Colors.text.h1} size={8} />
                :<></>
                }
            </Button>
            {expanded ?
            <View type='body'>
                <View nativeID="competition_type" style={{ borderBottomWidth:1, borderColor:Colors.borders.light, padding:20 }}>
                    <View>
                        <Text theme="h1">Competition Type</Text>
                        <Text style={{ marginTop:3 }} theme="description">{competition_type?competition_type.description:'Choose the type of competition this is.'}</Text>
                    </View>
                    <View style={{ margin:5 }}>
                        <Toggle
                            options={competition_types.map(t => {
                                return { key: t.competition_type_id, label: t.type_label }
                            })}
                            selected_option={competition_type?.competition_type_id}
                            onSelectOption={(option) => onCompetitionUpdate({ ...draft_competition, competition_type_id: option })}
                        />
                    </View>
                </View>
                <View style={{ padding:20, borderBottomWidth:1, borderColor:Colors.borders.light }}>
                    <View>
                        <Text theme="h1">Length Type</Text>
                        <Text style={{ marginTop:3 }}>Choose how long this competition will remain open.</Text>
                    </View>
                    <View style={{ margin:5 }}>
                        <Toggle
                            options={length_types.map(t => {
                                return { key: t, label: t }
                            })}
                            selected_option={competition.length_type}
                            onSelectOption={(option) => onCompetitionUpdate({ ...draft_competition, length_type: option as any })}
                        />
                    </View>
                </View>
                {competition_type?.type == 'pick' ?
                <View type='row' style={{ flexWrap:'wrap', padding:20, borderBottomWidth:1, borderColor:Colors.borders.light }}>
                    <View style={{ flex:1, minWidth:100, marginRight:10 }}>
                        <Text theme="h1">Max Picks</Text>
                        <Text style={{ marginTop:3 }}>The total number of picks allowed by each participant.</Text>
                    </View>
                    <TextInput
                        editable={competition.competition_season_id ? false : true}
                        style={{ flex:1, minWidth:100, textAlign:'center', margin:5 }}
                        onChangeText={(text) => setDraftCompetition({ ...draft_competition, max_pick_count: text })}
                        placeholder="0"
                        onFocus={() => {
                            if(draft_competition.max_pick_count == 0){ setDraftCompetition({ ...draft_competition, max_pick_count: '' }) }
                        }}
                        onFocusPosition={onFocusPosition}
                        keyboardType="decimal-pad"
                        value={draft_competition.max_pick_count.toString()}
                    />
                    {is_changed ?
                    <Button
                        title='SAVE'
                        type='success'
                        style={{ margin:5, padding:15, flexGrow:1 }}
                        onPress={() => onCompetitionUpdate(draft_competition)}
                    />
                    :<></>}
                </View>
                :<></>}
                {competition_type?.type == 'wager' ?
                <View type='row' style={{ flexWrap:'wrap', padding:20, borderBottomWidth:1, borderColor:Colors.borders.light }}>
                    <View style={{ flex:1, minWidth:100, marginRight:10 }}>
                        <Text theme="h1">Initial Balance</Text>
                        <Text style={{ marginTop:3 }}>Initial balance that each player will start with at the beginning of the competition.</Text>
                    </View>
                    <TextInput
                        editable={competition.competition_season_id ? false : true}
                        style={{  flex:1, minWidth:100, textAlign:'center', margin:5 }}
                        onChangeText={(text) => setDraftCompetition({ ...draft_competition, initial_balance: text })}
                        placeholder="0"
                        onFocusPosition={onFocusPosition}
                        keyboardType="decimal-pad"
                        value={draft_competition.initial_balance.toString()}
                    />
                    {is_changed ?
                    <Button
                        title='SAVE'
                        type='success'
                        style={{ margin:5, padding:15, flexGrow:1 }}
                        onPress={() => onCompetitionUpdate(draft_competition)}
                    />
                    :<></>}
                </View>
                :<></>}
                <View style={{ padding:20 }}>
                    <View type='row'>
                        <View style={{ flex:1 }}>
                            <Text theme="h1">Start Time Options</Text>
                            <Text style={{ marginTop:3 }}>When should this competition stop allowing entrants.</Text>
                        </View>
                    </View>
                    <View style={{ marginTop:10, borderRadius:4 }}>
                        {start_time_options.length > 0 ?
                        <FlatList
                            data={start_time_options}
                            renderItem={renderStartTimeOptions}
                            keyExtractor={(item) => item.start_time}
                        />
                        :
                        <Text theme="warning">Please add contests and markets to see potential start times</Text>
                        }
                    </View>
                </View>
            </View>
            :<></>}
        </View>
    )
}

export default ContestSettingsForm